// Gestores (7-oct-2026). «Propietario y liquidación» (components/Gestores/OwnerSettlementPanel.vue
// y su modal OwnerFormModal.vue): la tarjeta del anuncio donde el gestor dice para quién lo lleva,
// quién cobra y cómo se le liquida.
export const ownerSettlement = {
  head: {
    title: "Owner & settlement",
    sub: "If you manage this property for another owner, set who collects the payments, your commission and how you settle with them. This drives their settlements and your manager analytics.",
  },
  loading: "Loading settings…",
  loadError: {
    title: "Couldn't load the settings",
    text: "Check your connection and try again.",
    retry: "Retry",
  },
  master: {
    label: "I manage this property for an owner",
    hintOff: "The property is yours: its bookings count as your own revenue.",
    hintOn: "Its bookings are settled with the owner on the terms below.",
    kept: "Your previous terms are kept in case you turn this back on.",
    introTitle: "Once it's on:",
    intro1: "You choose the owner and who collects the booking payments.",
    intro2: "You set your commission, its VAT and who keeps the cleaning.",
    intro3: "You see right away how their settlement works out.",
  },
  owner: {
    title: "Owner",
    hint: "The person or company you settle this property with.",
    placeholder: "Choose an owner",
    new: "New owner",
    add: "Add owner",
    edit: "Edit",
    emptyTitle: "You don't have any owners yet",
    emptyText: "Add the owner of this property so you can settle with them.",
    missing: "Without an owner assigned, this property's settlement has no recipient.",
    pending: "Assigned when you save",
    taxId: "Tax ID",
    addTaxId: "Add tax ID",
    iban: "IBAN",
    addIban: "Add IBAN",
  },
  ownerForm: {
    titleNew: "New owner",
    titleEdit: "Edit owner",
    sub: "Their details appear on the settlements of the properties you manage for them.",
    close: "Close",
    name: "Name or company name",
    taxId: "Tax ID",
    taxIdHint: "Their tax identification number, from any country.",
    taxIdDoubt: "If this is a Spanish NIF, the check character doesn't match. If it's from another country, ignore this.",
    email: "Email",
    phone: "Phone",
    iban: "IBAN",
    ibanPlaceholder: "GB00 0000 0000 0000 0000 00",
    ibanHint: "The account you transfer their share to.",
    ibanOk: "Valid IBAN · {country}",
    address: "Address",
    notes: "Notes",
    notesPlaceholder: "What you agreed, payment day…",
    sharedNote: "They also own {n} other property: changes will show there too. | They also own {n} other properties: changes will show in all of them.",
    cancel: "Cancel",
    save: "Save owner",
    saving: "Saving…",
    created: "Owner created",
    updated: "Owner updated",
    deleted: "Owner deleted",
    errName: "Enter the name or company name.",
    errEmail: "Check the email: it doesn't look valid.",
    errIbanFormat: "An IBAN starts with the country code and two check digits (GB82…).",
    errIbanCheck: "The check digits don't match: make sure it was copied correctly.",
    errIbanLength: "An IBAN from {country} has {expected} characters and this one has {actual}.",
    errSave: "Couldn't save the owner. Please try again.",
    errDelete: "Couldn't delete the owner. Please try again.",
    delete: "Delete owner",
    deleteTitle: "Delete {name}?",
    deleteText: "They'll be removed from your list of owners. This can't be undone.",
    deleteConfirm: "Delete",
    deleteBlocked: "Can't delete: they're the owner of {n} property. Remove them from it first. | Can't delete: they're the owner of {n} properties. Remove them from each one first.",
    discardTitle: "Discard changes?",
    discardText: "What you've typed for this owner will be lost.",
    discardConfirm: "Discard",
    discardCancel: "Keep editing",
  },
  units: {
    title: "Owner of each unit",
    hint: "If a unit belongs to someone else, choose them here. The rest are settled with the listing's owner.",
    inherit: "Listing's owner",
    summaryNone: "All belong to the listing's owner",
    summarySome: "{n} unit with another owner | {n} units with another owner",
  },
  collection: {
    title: "Who collects the booking payments?",
    hint: "This changes where the money goes, not what the owner keeps.",
    ownerLabel: "The owner collects",
    ownerDesc: "The platforms and your website pay the owner. Then they pay your fee invoice (and cleaning, if it's yours).",
    managerLabel: "The manager collects",
    managerDesc: "The booking money comes to you. You keep your part and transfer the owner their share.",
    flowBooking: "Booking",
    flowOwner: "Owner",
    flowYou: "You",
  },
  commission: {
    title: "Your commission",
    percent: "Commission rate",
    vat: "VAT on your commission",
    base: "Calculated on",
    presetTotal: "Booking total",
    presetAccommodation: "Accommodation without cleaning",
    presetNet: "Net of OTA commission",
    presetDescNet: "{concepts}, minus their share of the OTA commission",
    presetNetClean: "Net of OTA and cleaning",
    presetDescNetClean: "What the owner keeps before your fee: the total minus the OTA, its VAT and cleaning",
    exampleBase: "base in the example",
    custom: "Custom",
    customize: "Fine-tune",
    baseLabel: "On which amount",
    gross: "What the guest pays",
    net: "What's left after the OTA commission",
    netClean: "What's left after the OTA and cleaning",
    concepts: "Which items count",
    warnTaxes: "Taxes aren't income: commission isn't usually charged on them.",
    fixTaxes: "Remove taxes",
    warnCleaning: "You're charging commission on cleaning you already keep.",
    fixCleaning: "Remove cleaning",
    warnDeposit: "The deposit goes back to the guest: commission isn't usually charged on it.",
    fixDeposit: "Remove deposit",
    errPercent: "Enter a percentage between 0 and 100.",
    errConcepts: "Choose at least one item.",
  },
  concepts: {
    accommodation: "Accommodation",
    cleaning: "Cleaning",
    taxes: "Taxes",
    extras: "Extras",
    deposit: "Deposit",
  },
  // Los mismos conceptos dentro de una frase («Base: alojamiento y extras.»)
  conceptsInline: {
    accommodation: "accommodation",
    cleaning: "cleaning",
    taxes: "taxes",
    extras: "extras",
    deposit: "deposit",
  },
  cleaning: {
    title: "Cleaning",
    hint: "Who keeps what the guest pays for cleaning?",
    owner: "It's the owner's",
    ownerDesc: "It stays in their net: nothing is deducted.",
    manager: "It's yours",
    managerDesc: "You handle it, and it's deducted from the owner.",
    charged: "What the guest pays for cleaning",
    fixed: "A fixed amount per stay",
    fixedAmount: "Amount per stay",
    fixedHint: "If a booking includes several rooms, it counts once per room.",
    errAmount: "Enter the fixed cleaning amount per stay.",
  },
  ota: {
    title: "Commission for each OTA",
    moved: "Each OTA's % is set in the “{card}” card.",
    open: "Open “{card}”",
  },
  example: {
    title: "Sample settlement",
    sub: "A {total} booking on {channel}, with your current terms.",
    guest: "What the guest pays",
    platform: "What {channel} keeps",
    platformNote: "Its commission: {pct}% of {base} ({concepts}).",
    platformYours: "Your percentages in “{card}” win, even if {channel} sends its commission.",
    platformReal: "If {channel} sends a booking's commission, that one is used.",
    platformRule: "If you set the {channel} percentage in “{card}”, yours wins; otherwise, its commission.",
    otaVat: "VAT on the {channel} commission ({pct}%)",
    changeIn: "Change in “{card}”",
    changeWhere: "Change it in “{card}”.",
    notOwner: "What isn't the owner's",
    taxes: "Taxes and charges",
    taxesNote: "They aren't the owner's income.",
    cleaning: "Cleaning you keep",
    cleaningNoteCharged: "What the guest pays for it.",
    cleaningNoteFixed: "Your fixed amount per stay.",
    fees: "Your management fees",
    commission: "Commission: {pct}% of {base}",
    commissionBase: "Base: {concepts}.",
    commissionBaseNet: "Base: {concepts}, minus their share of the {channel} commission.",
    commissionBaseNetClean: "Base: what the owner keeps after {channel}, its VAT and cleaning.",
    vat: "VAT on your commission ({pct}%)",
    invoice: "Total of your fee invoice",
    net: "Net for the owner",
    netNote: "What's theirs from this booking.",
    netNegative: "The deductions exceed what the booking leaves.",
    splitTitle: "How the {total} is split",
    legendTaxes: "Taxes",
    legendCleaning: "Cleaning (yours)",
    legendFees: "Your fees",
    legendOwner: "Owner",
    legendDeficit: "Shortfall",
    transferTitle: "You transfer {amount}",
    transferTitleTo: "You transfer {amount} to {owner}",
    transferInvoice: "And you issue your fee invoice for {amount}.",
    owesTitle: "The owner owes you {amount}",
    owesTitleFrom: "{owner} owes you {amount}",
    owesInvoice: "There's nothing to transfer. Your fee invoice is {amount}.",
    owesHint: "In this example, the deductions exceed what the booking leaves.",
    collectTitle: "The owner pays you {amount}",
    collectTitleFrom: "{owner} pays you {amount}",
    collectBreakdown: "That's your fee invoice ({fees}).",
    collectBreakdownCleaning: "Your fee invoice ({fees}) plus the cleaning ({cleaning}).",
    ownerLoses: "On these terms, the owner loses {amount} on this booking.",
    foot: "Sample amounts. The actual settlement uses each booking's breakdown.",
    footNoOtaVat: "Without the VAT on the OTA commission.",
  },
  footer: {
    save: "Save changes",
    saving: "Saving…",
    unsaved: "Unsaved changes",
    saved: "Saved",
    fixErrors: "Check the highlighted fields.",
    errSave: "Couldn't save. Please try again.",
    peekLabel: "Left for the owner",
    peekAria: "See the sample settlement: {amount} left for the owner",
  },
};
